// Support: het overzicht van de supportmailbox (alleen metadata, door de worker gesynchroniseerd) en de koppeling van
// afzenderdomeinen aan klanten (alleen planners). Datums zijn ISO-tijdstippen.
import { useQuery } from "@tanstack/react-query";
import { call } from "./api";

export interface SupportDevRef {
  /** personId, of "cat:<naam>" voor een categorie zonder persoon */
  key: string;
  personId: string | null;
  name: string;
  /** de Outlook-categorie zoals ze in de mailbox staat */
  category: string;
}

export interface SupportDevRow extends SupportDevRef {
  /** afgehandelde gesprekken per week (12 weken, oudste eerst) */
  cells: number[];
  total: number;
  open: number;
}

export interface SupportKlantRow {
  /** projectId, of "" voor gesprekken zonder klant */
  key: string;
  projectId: string | null;
  name: string;
  domains: string[];
  cells: number[];
  total: number;
  open: number;
}

/** gekoppeld: aan een klant; onbekend: nog niet gekoppeld; genegeerd: platform, geen klant; eigen: enkel TLCC */
export type DomainState = "gekoppeld" | "onbekend" | "genegeerd" | "eigen";

export interface SupportConversation {
  id: string;
  subject: string;
  from: string;
  domain: string;
  domainState: DomainState;
  klantId: string | null;
  klant: string | null;
  devs: SupportDevRef[];
  messages: number;
  /** eerste mail van het gesprek */
  received: string;
  /** oudste mail in de Inbox: sindsdien staat het open */
  openSince: string;
  /** opgenomen (benadering); null als de sync de categorie niet in de Inbox zag verschijnen */
  picked: string | null;
  webLink: string | null;
}

export interface SupportSync {
  mailbox: string;
  /** nooit: nog niet gelezen; toegang: de app mag de mailbox niet lezen; fout: laatste ronde mislukt; uit: geen mailbox */
  state: "ok" | "nooit" | "toegang" | "fout" | "uit";
  lastOk: string | null;
  /** de eerste lezing loopt nog */
  busy: boolean;
  error: string;
  errorAt: string | null;
}

export interface UnknownDomain {
  domain: string;
  conversations: number;
  open: number;
  lastReceived: string;
}

export interface SupportOverview {
  now: string;
  sync: SupportSync;
  weeks: { nr: number; monday: string }[];
  devs: SupportDevRow[];
  klanten: SupportKlantRow[];
  /** afgehandelde gesprekken per week, elk gesprek één keer */
  team: number[];
  grand: number;
  /** mediaan in uren; null als niet af te leiden */
  lead: { total: number | null; pick: number | null; done: number | null; n: number; nPick: number };
  open: SupportConversation[];
  unknownDomains: UnknownDomain[];
}

export interface DomainLinkRow {
  domain: string;
  /** null = genegeerd */
  projectId: string | null;
  klant: string | null;
  conversations: number;
  createdBy: string;
  createdAt: string;
}

export const supportApi = {
  overview: (klant: string | null) => call<SupportOverview>("GET", "/api/support" + (klant ? "?klant=" + encodeURIComponent(klant) : "")),
  domainLinks: () => call<{ links: DomainLinkRow[]; unknown: { domain: string; conversations: number }[] }>("GET", "/api/domain-links"),
  /** projectId null: negeren (platform of leverancier, telt niet mee per klant) */
  link: (domain: string, projectId: string | null) => call<{ ok: boolean; changeId: number }>("POST", "/api/domain-links", { domain, projectId }),
  unlink: (domain: string) => call<{ ok: boolean; changeId: number }>("DELETE", "/api/domain-links", { domain }),
};

export function useSupport(klant: string | null) {
  // bij een andere klant blijft het vorige overzicht staan tot het nieuwe er is
  return useQuery({ queryKey: ["support", klant], queryFn: () => supportApi.overview(klant), refetchInterval: 60e3, staleTime: 30e3, placeholderData: (prev) => prev });
}

export function useDomainLinks(enabled = true) {
  return useQuery({ queryKey: ["domain-links"], queryFn: supportApi.domainLinks, enabled, staleTime: 30e3 });
}
